import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import {
  approveManagementOrder,
  createOrderShipment,
  deleteOrderShipment,
  downloadOrderPaymentMemoPdf,
  getManagementOrder,
  patchManagementOrderLineCustomText,
  patchOrderShipment,
  refundManagementOrder,
  rejectManagementOrder,
  reopenManagementOrder,
  resolveOrderPaymentMemo,
  updateManagementOrder,
} from '../../../endpoints/chapterSupplies';
import { getCountries, getStates } from '../../../endpoints/lookupsTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import type {
  ManagementOrderDetailResponse,
  OrderShipmentFulfillmentLineResponse,
  StorefrontCheckoutLineResponse,
} from '../../../types/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const roundMoney = (v: number) => Math.round(v * 100) / 100;

const formatBatchType = (batchType?: string) => {
  switch (batchType) {
    case 'chapter_pins':
      return 'Chapter pins';
    case 'state_pins':
      return 'State pins';
    default:
      return batchType || '—';
  }
};

/** Matches peo-hub-go chapter_supplies.CarrierUSPS */
const DEFAULT_SHIPMENT_CARRIER_ID = '6af9b847433341bba003514ec69bb8a5';

const CARRIER_OPTIONS: { id: string; label: string }[] = [
  { id: '40102e8d6d264d6d9c3ad810f6354cac', label: 'UPS' },
  { id: DEFAULT_SHIPMENT_CARRIER_ID, label: 'USPS' },
  { id: '7acb0bf2a4584ee0965e721362b5de3d', label: 'FedEx' },
];

function carrierLabel(carrierId: string): string {
  return CARRIER_OPTIONS.find((c) => c.id === carrierId)?.label || carrierId;
}

function lineDisplayName(
  lines: StorefrontCheckoutLineResponse[],
  orderLineItemId: string
): string {
  const match = lines.find((l) => l.order_line_item_id === orderLineItemId);
  const name = match?.item_display_name?.trim() || match?.item_name?.trim();
  return name || orderLineItemId;
}

function fulfillmentRowForLine(
  fulfillment: OrderShipmentFulfillmentLineResponse[] | undefined,
  orderLineItemId: string
): OrderShipmentFulfillmentLineResponse | undefined {
  return fulfillment?.find((r) => r.order_line_item_id === orderLineItemId);
}

function remainingToAllocateForLine(
  fulfillment: OrderShipmentFulfillmentLineResponse[] | undefined,
  line: StorefrontCheckoutLineResponse
): number {
  const key = line.order_line_item_id;
  if (!key) return Math.max(0, line.quantity || 0);
  const fr = fulfillmentRowForLine(fulfillment, key);
  if (fr?.is_vendor_direct) {
    return 0;
  }
  const row = fulfillment?.find((r) => r.order_line_item_id === key);
  if (!row) return Math.max(0, line.quantity || 0);
  return Math.max(
    0,
    row.quantity_ordered - row.quantity_shipped - row.quantity_allocated_draft
  );
}

function defaultBoxQtyForLine(
  fulfillment: OrderShipmentFulfillmentLineResponse[] | undefined,
  line: StorefrontCheckoutLineResponse,
  newBoxQty: Record<string, string>,
  key: string
): string {
  if (Object.prototype.hasOwnProperty.call(newBoxQty, key)) {
    return newBoxQty[key];
  }
  const remaining = remainingToAllocateForLine(fulfillment, line);
  return remaining > 0 ? String(remaining) : '';
}

function parseBoxQtyForLine(
  fulfillment: OrderShipmentFulfillmentLineResponse[] | undefined,
  line: StorefrontCheckoutLineResponse,
  newBoxQty: Record<string, string>,
  key: string
): number {
  const raw = defaultBoxQtyForLine(fulfillment, line, newBoxQty, key);
  const q = parseInt(raw, 10);
  return Number.isFinite(q) && q > 0 ? q : 0;
}

// Match chapter-supplies order_statuses IDs / names (see peo-hub-go internal/features/chapter_supplies/constants.go).
const ORDER_STATUS_PENDING_APPROVAL_ID = '317bce051aa54ba8a20453d8e7d422cd';
const ORDER_STATUS_REJECTED_ID = 'fb904cd1c53f41b198727b3687b02900';
const ORDER_STATUS_SHIPPED_ID = '5644eeaa8ce64c91ab918f37f565c3e1';
const ORDER_STATUS_CANCELLED_ID = 'baa4cae4937142b5bd6e42e39f323645';

function isPendingApprovalStatus(
  statusName: string,
  statusId: string
): boolean {
  const slug = statusName.trim().toLowerCase();
  if (slug === 'pending_approval') return true;
  return statusId.trim() === ORDER_STATUS_PENDING_APPROVAL_ID;
}

function isRejectedStatus(statusName: string, statusId: string): boolean {
  const slug = statusName.trim().toLowerCase();
  if (slug === 'rejected') return true;
  return statusId.trim() === ORDER_STATUS_REJECTED_ID;
}

function orderStatusAllowsApproverOrderEdits(
  statusName: string,
  statusId: string
): boolean {
  const slug = statusName.trim().toLowerCase();
  const sid = statusId.trim();
  if (slug === 'shipped' || sid === ORDER_STATUS_SHIPPED_ID) return false;
  if (slug === 'cancelled' || sid === ORDER_STATUS_CANCELLED_ID) return false;
  if (isRejectedStatus(statusName, statusId)) return false;
  return true;
}

function orderStatusAllowsCustomTextEdit(
  statusName: string,
  statusId: string
): boolean {
  return orderStatusAllowsApproverOrderEdits(statusName, statusId);
}

function needsFreightPaymentMismatchConfirm(
  data: ManagementOrderDetailResponse,
  newTotal: number
): boolean {
  if (roundMoney(newTotal) === roundMoney(data.total)) return false;
  if ((data.stripe_amount_captured ?? 0) > 0) return true;
  const checkReceived =
    data.payment_memo?.check_amount_received ?? data.check_amount_received ?? 0;
  if (checkReceived > 0 && roundMoney(checkReceived) !== roundMoney(newTotal)) {
    return true;
  }
  return false;
}

function lineAllowsCustomTextEditor(
  line: StorefrontCheckoutLineResponse
): boolean {
  const ct = line.allows_custom_text ?? 0;
  const pers = line.allows_personalization ?? 0;
  if (ct !== 0 || pers !== 0) return true;
  return (line.custom_text_lines?.length ?? 0) > 0;
}

export default function ManagementOrderDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-management-order', id],
    () => getManagementOrder(id!),
    { enabled: !!id }
  );
  const { data: myPermissions = [] } = useApiGet(
    ['chapter-supplies-order-detail-perms'],
    () => getMyPermissions({ international: '1' }),
    { staleTime: 60_000 }
  );
  const canShip = React.useMemo(() => {
    const s = new Set(myPermissions);
    return s.has('orders:shipment');
  }, [myPermissions]);

  const canApprove = React.useMemo(() => {
    const s = new Set(myPermissions);
    return s.has('orders:approver');
  }, [myPermissions]);

  const canRefundPermission = React.useMemo(() => {
    const s = new Set(myPermissions);
    return s.has('orders:refund');
  }, [myPermissions]);

  const [refundDialogOpen, setRefundDialogOpen] = React.useState(false);
  const [memoResolveOpen, setMemoResolveOpen] = React.useState(false);
  const [memoResolveAmount, setMemoResolveAmount] = React.useState('');
  const [memoResolveNotes, setMemoResolveNotes] = React.useState('');
  const [memoResolveCheckNumber, setMemoResolveCheckNumber] =
    React.useState('');
  const [memoResolveBusy, setMemoResolveBusy] = React.useState(false);
  const [memoResolveError, setMemoResolveError] = React.useState<string | null>(
    null
  );
  const [memoPdfBusy, setMemoPdfBusy] = React.useState(false);
  const [memoPdfError, setMemoPdfError] = React.useState<string | null>(null);
  const [refundMode, setRefundMode] = React.useState<'full' | 'partial'>(
    'full'
  );
  const [partialRefundAmount, setPartialRefundAmount] = React.useState('');
  const [refundBusy, setRefundBusy] = React.useState(false);
  const [refundError, setRefundError] = React.useState<string | null>(null);
  const [refundSuccess, setRefundSuccess] = React.useState<string | null>(null);
  const hasRemainingToAllocate = React.useMemo(() => {
    if (!data?.shipping) return false;
    return data.lines.some((line) => {
      const fr = fulfillmentRowForLine(
        data.shipping?.fulfillment,
        line.order_line_item_id ?? ''
      );
      if (fr?.is_vendor_direct) return false;
      return remainingToAllocateForLine(data.shipping?.fulfillment, line) > 0;
    });
  }, [data?.lines, data?.shipping]);

  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [shipErr, setShipErr] = React.useState<string | null>(null);
  const [shipBusy, setShipBusy] = React.useState(false);
  const [newBoxQty, setNewBoxQty] = React.useState<Record<string, string>>({});
  const [customTextEditLineId, setCustomTextEditLineId] = React.useState<
    string | null
  >(null);
  const [customTextDraft, setCustomTextDraft] = React.useState('');
  const [customTextEditBusy, setCustomTextEditBusy] = React.useState(false);
  const [customTextEditError, setCustomTextEditError] = React.useState<
    string | null
  >(null);
  const [freightEditing, setFreightEditing] = React.useState(false);
  const [freightDraft, setFreightDraft] = React.useState('');
  const [freightNoCharge, setFreightNoCharge] = React.useState(false);
  const [freightAmountBeforeNoCharge, setFreightAmountBeforeNoCharge] =
    React.useState('');
  const [freightEditBusy, setFreightEditBusy] = React.useState(false);
  const [freightEditError, setFreightEditError] = React.useState<string | null>(
    null
  );
  const [freightConfirmOpen, setFreightConfirmOpen] = React.useState(false);
  const [freightPendingTotal, setFreightPendingTotal] = React.useState<
    number | null
  >(null);
  const [draftForms, setDraftForms] = React.useState<
    Record<string, { carrier_id: string; tracking: string; notes: string }>
  >({});

  React.useEffect(() => {
    if (!data?.shipping) {
      setDraftForms({});
      return;
    }
    const m: Record<
      string,
      { carrier_id: string; tracking: string; notes: string }
    > = {};
    for (const sh of data.shipping.shipments) {
      if (!sh.shipped_at) {
        m[sh.id] = {
          carrier_id: sh.carrier_id || DEFAULT_SHIPMENT_CARRIER_ID,
          tracking: sh.tracking_number || '',
          notes: sh.notes || '',
        };
      }
    }
    setDraftForms(m);
  }, [data?.shipping]);

  React.useEffect(() => {
    if (!data?.lines || !data?.shipping || !hasRemainingToAllocate) return;
    setNewBoxQty((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const line of data.lines) {
        const key = line.order_line_item_id;
        if (!key) continue;
        const fr = fulfillmentRowForLine(data.shipping?.fulfillment, key);
        if (fr?.is_vendor_direct) continue;
        const remaining = remainingToAllocateForLine(
          data.shipping?.fulfillment,
          line
        );
        if (remaining > 0 && !Object.prototype.hasOwnProperty.call(prev, key)) {
          next[key] = String(remaining);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data?.lines, data?.shipping, hasRemainingToAllocate]);

  const runAction = async (action: 'approve' | 'reject' | 'reopen') => {
    if (!id) return;
    setActionLoading(true);
    setActionError(null);
    try {
      if (action === 'approve') await approveManagementOrder(id);
      if (action === 'reject') await rejectManagementOrder(id);
      if (action === 'reopen') await reopenManagementOrder(id);
      navigate(0);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to update order status.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const invalidateOrder = React.useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['chapter-supplies-management-order', id],
    });
  }, [id, queryClient]);

  const showFreightEdit =
    canApprove &&
    !!data &&
    orderStatusAllowsApproverOrderEdits(
      data.order_status_name,
      data.order_status_id
    );

  const resolveFreightDraftAmount = React.useCallback((): number | null => {
    if (freightNoCharge) return 0;
    const freight = parseFloat(freightDraft);
    if (Number.isNaN(freight) || freight < 0) return null;
    return freight;
  }, [freightDraft, freightNoCharge]);

  const freightPreviewTotal = React.useMemo(() => {
    if (!data || !freightEditing) return data?.total ?? 0;
    const freight = resolveFreightDraftAmount();
    if (freight === null) return data.total;
    return roundMoney(data.subtotal + freight + data.tax);
  }, [data, freightEditing, resolveFreightDraftAmount]);

  const resetFreightEdit = React.useCallback(() => {
    setFreightEditing(false);
    setFreightDraft('');
    setFreightNoCharge(false);
    setFreightAmountBeforeNoCharge('');
    setFreightEditError(null);
  }, []);

  const startFreightEdit = React.useCallback(() => {
    if (!data) return;
    const amount = data.shipping_amount ?? 0;
    const amountStr = String(amount);
    setFreightEditing(true);
    setFreightDraft(amountStr);
    setFreightNoCharge(amount === 0);
    setFreightAmountBeforeNoCharge(amountStr);
    setFreightEditError(null);
  }, [data]);

  const persistFreight = async (shippingAmount: number, total: number) => {
    if (!id) return;
    setFreightEditBusy(true);
    setFreightEditError(null);
    try {
      await updateManagementOrder(id, {
        shipping_amount: shippingAmount,
        total,
      });
      setFreightEditing(false);
      setFreightDraft('');
      setFreightNoCharge(false);
      setFreightAmountBeforeNoCharge('');
      setFreightConfirmOpen(false);
      setFreightPendingTotal(null);
      invalidateOrder();
    } catch (e) {
      setFreightEditError(getApiErrorMessage(e) || 'Unable to save freight.');
    } finally {
      setFreightEditBusy(false);
    }
  };

  const handleFreightSave = () => {
    if (!data) return;
    const freight = resolveFreightDraftAmount();
    if (freight === null) {
      setFreightEditError('Enter a valid freight amount (0 or greater).');
      return;
    }
    const newTotal = roundMoney(data.subtotal + freight + data.tax);
    if (needsFreightPaymentMismatchConfirm(data, newTotal)) {
      setFreightPendingTotal(newTotal);
      setFreightConfirmOpen(true);
      return;
    }
    void persistFreight(freight, newTotal);
  };

  const showRefundButton = canRefundPermission && data?.can_refund === true;

  const openMemoResolveDialog = () => {
    const memo = data?.payment_memo;
    if (!memo) return;
    setMemoResolveError(null);
    setMemoResolveAmount(
      memo.balance_remaining > 0 ? String(memo.balance_remaining) : ''
    );
    setMemoResolveNotes('');
    setMemoResolveCheckNumber('');
    setMemoResolveOpen(true);
  };

  const submitMemoResolve = async () => {
    if (!id || !data?.payment_memo) return;
    setMemoResolveBusy(true);
    setMemoResolveError(null);
    try {
      const parsed = parseFloat(memoResolveAmount.replace(/,/g, ''));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setMemoResolveError('Enter a valid amount greater than zero.');
        return;
      }
      if (parsed > data.payment_memo.balance_remaining + 0.001) {
        setMemoResolveError(
          `Amount cannot exceed ${formatPrice(data.payment_memo.balance_remaining)} remaining.`
        );
        return;
      }
      const isDebit = data.payment_memo.memo_type === 'debit';
      const resolutionType = isDebit ? 'payment_received' : 'credit_issued';
      const checkNum = memoResolveCheckNumber.trim();
      if (isDebit && !checkNum) {
        setMemoResolveError(
          'Check number is required when recording payment received.'
        );
        return;
      }
      if (checkNum.length > 32) {
        setMemoResolveError('Check number must be at most 32 characters.');
        return;
      }
      await resolveOrderPaymentMemo(id, {
        amount_applied: parsed,
        resolution_type: resolutionType,
        check_number: isDebit ? checkNum : undefined,
        notes: memoResolveNotes.trim() ? memoResolveNotes.trim() : null,
      });
      setMemoResolveOpen(false);
      invalidateOrder();
    } catch (e) {
      setMemoResolveError(
        getApiErrorMessage(e) || 'Unable to apply memo resolution.'
      );
    } finally {
      setMemoResolveBusy(false);
    }
  };

  const handleMemoPdfDownload = async () => {
    if (!id) return;
    setMemoPdfBusy(true);
    setMemoPdfError(null);
    try {
      const blob = await downloadOrderPaymentMemoPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-memo-${data?.order_number ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setMemoPdfError(getApiErrorMessage(e) || 'Could not download memo PDF.');
    } finally {
      setMemoPdfBusy(false);
    }
  };

  const openRefundDialog = () => {
    setRefundError(null);
    setRefundMode('full');
    setPartialRefundAmount('');
    setRefundDialogOpen(true);
  };

  const submitRefund = async () => {
    if (!id || !data) return;
    setRefundBusy(true);
    setRefundError(null);
    try {
      const refundable = data.stripe_refundable_amount ?? 0;
      let body: { amount?: number } = {};
      if (refundMode === 'partial') {
        const parsed = parseFloat(partialRefundAmount.replace(/,/g, ''));
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setRefundError('Enter a valid refund amount greater than zero.');
          return;
        }
        if (parsed > refundable + 0.001) {
          setRefundError(
            `Amount cannot exceed ${formatPrice(refundable)} refundable.`
          );
          return;
        }
        body = { amount: parsed };
      }
      const resp = await refundManagementOrder(id, body);
      setRefundDialogOpen(false);
      setRefundSuccess(
        `Refunded ${formatPrice(resp.amount_refunded)}. Remaining refundable: ${formatPrice(resp.stripe_refundable_amount)}.`
      );
      invalidateOrder();
    } catch (e) {
      setRefundError(
        e instanceof Error ? e.message : 'Unable to process refund.'
      );
    } finally {
      setRefundBusy(false);
    }
  };

  const saveCustomTextLines = async (lineId: string) => {
    if (!id) return;
    setCustomTextEditBusy(true);
    setCustomTextEditError(null);
    const lines = customTextDraft
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    try {
      await patchManagementOrderLineCustomText(id, lineId, {
        custom_text_lines: lines,
      });
      setCustomTextEditLineId(null);
      setCustomTextDraft('');
      invalidateOrder();
    } catch (e) {
      setCustomTextEditError(
        e instanceof Error ? e.message : 'Unable to save custom text.'
      );
    } finally {
      setCustomTextEditBusy(false);
    }
  };

  const handleCreateShipment = async () => {
    if (!id || !data?.lines) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      const linesPayload = data.lines
        .map((line) => {
          const key = line.order_line_item_id;
          if (!key) return null;
          const fr = fulfillmentRowForLine(data.shipping?.fulfillment, key);
          if (fr?.is_vendor_direct) return null;
          const q = parseBoxQtyForLine(
            data.shipping?.fulfillment,
            line,
            newBoxQty,
            key
          );
          if (!q) return null;
          return { order_line_item_id: key, quantity: q };
        })
        .filter(Boolean) as { order_line_item_id: string; quantity: number }[];
      if (linesPayload.length === 0) {
        setShipErr('Enter a positive quantity for at least one line.');
        return;
      }
      await createOrderShipment(id, { lines: linesPayload });
      setNewBoxQty({});
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to create shipment.');
    } finally {
      setShipBusy(false);
    }
  };

  const handleMarkShipped = async (shipmentId: string) => {
    if (!id) return;
    const f = draftForms[shipmentId];
    if (!f) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      await patchOrderShipment(id, shipmentId, {
        carrier_id: f.carrier_id,
        tracking_number: f.tracking.trim(),
        notes: f.notes.trim() || undefined,
        mark_shipped: true,
      });
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to mark shipped.');
    } finally {
      setShipBusy(false);
    }
  };

  const handleDeleteDraft = async (shipmentId: string) => {
    if (!id) return;
    setShipBusy(true);
    setShipErr(null);
    try {
      await deleteOrderShipment(id, shipmentId);
      invalidateOrder();
    } catch (e) {
      setShipErr(e instanceof Error ? e.message : 'Unable to delete shipment.');
    } finally {
      setShipBusy(false);
    }
  };

  const { data: countries = [] } = useApiGet(
    ['management-order-detail-countries'],
    getCountries
  );
  const { data: states = [] } = useApiGet(
    ['management-order-detail-states', data?.ship_to_country_id || ''],
    () => getStates(data?.ship_to_country_id || ''),
    { enabled: !!data?.ship_to_country_id }
  );

  const resolvedCountryName =
    (data?.ship_to_country_name && data.ship_to_country_name.trim()) ||
    countries.find((country) => country.id === data?.ship_to_country_id)
      ?.name ||
    'Unknown country';
  const resolvedStateName =
    (data?.ship_to_state_name && data.ship_to_state_name.trim()) ||
    states.find((state) => state.id === data?.ship_to_state_id)?.name ||
    'Unknown state/province';

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies-management/orders')}
        sx={{ mb: 2 }}
      >
        Back to orders
      </Button>
      {isLoading && <Typography>Loading order...</Typography>}
      {error && (
        <Typography color="error">Unable to load order details.</Typography>
      )}
      {data && (
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {data.order_number != null
                ? `Order #${data.order_number}`
                : `Order ${data.order_id}`}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {new Date(data.placed_at).toLocaleString()} —{' '}
              {data.order_status_name || data.order_status_id}
            </Typography>
            {(data.stripe_payment_intent_id ||
              data.stripe_payment_status ||
              data.stripe_payment_id) && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}
                useFlexGap
              >
                {data.stripe_payment_status && (
                  <Chip
                    size="small"
                    label={`Stripe: ${data.stripe_payment_status}`}
                    variant="outlined"
                  />
                )}
                {data.stripe_payment_intent_id && (
                  <Chip
                    size="small"
                    label={data.stripe_payment_intent_id}
                    title="PaymentIntent id"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                )}
                {data.stripe_payment_id && (
                  <Chip
                    size="small"
                    label={`Hub payment ${data.stripe_payment_id}`}
                    title="Internal stripe_payments.id"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                )}
              </Stack>
            )}
            {data.is_vendor_direct && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {data.shipping
                  ? 'This order mixes vendor-direct emblem fulfillment (Herff Jones) with items you ship from inventory. Only internal lines can be added to shipment boxes below.'
                  : 'This is a vendor-direct emblem order. It bypasses the internal shipment workflow.'}
              </Alert>
            )}
            {data.approved_at && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Approved: {new Date(data.approved_at).toLocaleString()}
                {data.approved_by_id ? ` by ${data.approved_by_id}` : ''}
              </Typography>
            )}
            {canApprove &&
              (isPendingApprovalStatus(
                data.order_status_name || '',
                data.order_status_id
              ) ||
                isRejectedStatus(
                  data.order_status_name || '',
                  data.order_status_id
                )) && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {isPendingApprovalStatus(
                    data.order_status_name || '',
                    data.order_status_id
                  ) && (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => void runAction('approve')}
                        disabled={actionLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => void runAction('reject')}
                        disabled={actionLoading}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {isRejectedStatus(
                    data.order_status_name || '',
                    data.order_status_id
                  ) && (
                    <Button
                      variant="outlined"
                      onClick={() => void runAction('reopen')}
                      disabled={actionLoading}
                    >
                      Reopen
                    </Button>
                  )}
                </Stack>
              )}
            {actionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {actionError}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
              Purchaser
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {data.purchaser_display_name}
              {data.purchaser_email ? (
                <>
                  <br />
                  {data.purchaser_email}
                </>
              ) : null}
              <br />
              User ID: {data.user_id}
            </Typography>

            {data.payment_memo && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Payment memo
                </Typography>
                {memoPdfError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {memoPdfError}
                  </Alert>
                )}
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Memo #{data.payment_memo.memo_number} —{' '}
                    {data.payment_memo.memo_type === 'credit'
                      ? 'Credit (overpayment)'
                      : 'Debit (underpayment)'}{' '}
                    — {data.payment_memo.status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Order total: {formatPrice(data.payment_memo.order_total)} ·
                    Check received:{' '}
                    {formatPrice(data.payment_memo.check_amount_received)}
                    {data.check_number ? ` (#${data.check_number})` : ''}
                  </Typography>
                  <Typography variant="body2">
                    Memo amount: {formatPrice(data.payment_memo.amount)} ·
                    Balance remaining:{' '}
                    {formatPrice(data.payment_memo.balance_remaining)}
                  </Typography>
                  {(data.payment_memo.resolutions?.length ?? 0) > 0 && (
                    <Table size="small" sx={{ maxWidth: 560 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Applied</TableCell>
                          <TableCell>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.payment_memo.resolutions?.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell>
                              {new Date(r.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {formatPrice(r.amount_applied)}
                            </TableCell>
                            <TableCell>{r.resolution_type}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {data.payment_memo.can_resolve_memo && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={openMemoResolveDialog}
                      >
                        Resolve memo
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={memoPdfBusy}
                      onClick={() => void handleMemoPdfDownload()}
                    >
                      {memoPdfBusy ? 'Downloading…' : 'Download memo PDF'}
                    </Button>
                  </Stack>
                </Stack>
              </>
            )}

            {data.is_vendor_direct && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Vendor fulfillment
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Mode: {data.fulfillment_mode || 'vendor_direct'}
                  {data.vendor_name ? (
                    <>
                      <br />
                      Vendor: {data.vendor_name}
                    </>
                  ) : null}
                  {data.vendor_batches && data.vendor_batches.length > 0 ? (
                    <>
                      <br />
                      Related batches: {data.vendor_batches.length}
                    </>
                  ) : null}
                </Typography>
                {data.vendor_batches && data.vendor_batches.length > 0 && (
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {data.vendor_batches.map((batch) => (
                      <Box
                        key={batch.batch_id}
                        sx={{
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {formatBatchType(batch.batch_type)}
                          {batch.po_number ? ` | PO ${batch.po_number}` : ''}
                          <br />
                          Status: {batch.batch_status}
                          {batch.sent_at ? (
                            <>
                              <br />
                              Sent: {new Date(batch.sent_at).toLocaleString()}
                            </>
                          ) : null}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/chapter-supplies-management/vendor-batches/${batch.batch_id}`
                            )
                          }
                        >
                          View vendor batch
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                )}
              </>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
              Ship to
            </Typography>
            <Typography variant="body2">
              {data.ship_to_name}
              <br />
              {data.ship_to_address_line1}
              {data.ship_to_address_line2
                ? `, ${data.ship_to_address_line2}`
                : ''}
              <br />
              {data.ship_to_city}, {resolvedStateName}{' '}
              {data.ship_to_postal_code}
              <br />
              {resolvedCountryName}
            </Typography>

            {canShip && !data.shipping && !data.is_vendor_direct && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                Fulfillment details load when your account has the
                orders:shipment permission.
              </Typography>
            )}

            {canShip && data.shipping && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Fulfillment
                </Typography>
                {shipErr && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {shipErr}
                  </Alert>
                )}
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Line progress
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">In draft boxes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.shipping.fulfillment.map((row) => (
                      <TableRow key={row.order_line_item_id}>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <span>
                              {lineDisplayName(
                                data.lines,
                                row.order_line_item_id
                              )}
                            </span>
                            {row.is_vendor_direct ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Vendor (Herff Jones)
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_ordered}
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_shipped}
                        </TableCell>
                        <TableCell align="right">
                          {row.quantity_allocated_draft}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Shipments
                </Typography>
                {data.shipping.shipments.length === 0 ? (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    No boxes yet.
                  </Typography>
                ) : (
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    {data.shipping.shipments.map((sh) => {
                      const isDraft = !sh.shipped_at;
                      const f = draftForms[sh.id];
                      return (
                        <Box
                          key={sh.id}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Box {sh.id.slice(0, 8)}…
                            {isDraft ? (
                              <Typography
                                component="span"
                                variant="caption"
                                color="warning.main"
                                sx={{ ml: 1 }}
                              >
                                Draft
                              </Typography>
                            ) : (
                              <Typography
                                component="span"
                                variant="caption"
                                color="success.main"
                                sx={{ ml: 1 }}
                              >
                                Shipped{' '}
                                {sh.shipped_at
                                  ? new Date(sh.shipped_at).toLocaleString()
                                  : ''}
                              </Typography>
                            )}
                          </Typography>
                          {sh.lines.map((ln) => (
                            <Typography
                              key={ln.id}
                              variant="body2"
                              color="text.secondary"
                            >
                              {lineDisplayName(
                                data.lines,
                                ln.order_line_item_id
                              )}{' '}
                              × {ln.quantity}
                            </Typography>
                          ))}
                          {isDraft && f && (
                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel id={`carr-${sh.id}`}>
                                  Carrier
                                </InputLabel>
                                <Select
                                  labelId={`carr-${sh.id}`}
                                  label="Carrier"
                                  value={f.carrier_id}
                                  onChange={(e) =>
                                    setDraftForms((prev) => ({
                                      ...prev,
                                      [sh.id]: {
                                        ...f,
                                        carrier_id: String(e.target.value),
                                      },
                                    }))
                                  }
                                >
                                  {CARRIER_OPTIONS.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                      {c.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <TextField
                                size="small"
                                label="Tracking number"
                                value={f.tracking}
                                onChange={(e) =>
                                  setDraftForms((prev) => ({
                                    ...prev,
                                    [sh.id]: { ...f, tracking: e.target.value },
                                  }))
                                }
                                fullWidth
                              />
                              <TextField
                                size="small"
                                label="Notes (optional)"
                                value={f.notes}
                                onChange={(e) =>
                                  setDraftForms((prev) => ({
                                    ...prev,
                                    [sh.id]: { ...f, notes: e.target.value },
                                  }))
                                }
                                fullWidth
                                multiline
                                minRows={2}
                              />
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="contained"
                                  disabled={shipBusy}
                                  onClick={() => void handleMarkShipped(sh.id)}
                                >
                                  Mark shipped
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={shipBusy}
                                  onClick={() => void handleDeleteDraft(sh.id)}
                                >
                                  Delete draft
                                </Button>
                              </Stack>
                            </Stack>
                          )}
                          {!isDraft && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {carrierLabel(sh.carrier_id || '')} —{' '}
                              {sh.tracking_number || '—'}
                              {sh.notes ? (
                                <>
                                  <br />
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {sh.notes}
                                  </Typography>
                                </>
                              ) : null}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                {hasRemainingToAllocate && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      New box (draft)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Enter quantities for this box, then create. You can add
                      carrier and tracking when marking shipped.
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {data.lines.map((line) => {
                        const key = line.order_line_item_id;
                        if (!key) return null;
                        const fr = fulfillmentRowForLine(
                          data.shipping?.fulfillment,
                          key
                        );
                        if (fr?.is_vendor_direct) {
                          return (
                            <Stack
                              key={key}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {(
                                  line.item_display_name ||
                                  line.item_name ||
                                  'Item'
                                ).trim()}{' '}
                                × {line.quantity}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  (vendor — Herff Jones)
                                </Typography>
                              </Typography>
                            </Stack>
                          );
                        }
                        const remaining = remainingToAllocateForLine(
                          data.shipping?.fulfillment,
                          line
                        );
                        return (
                          <Stack
                            key={key}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {(
                                line.item_display_name ||
                                line.item_name ||
                                'Item'
                              ).trim()}{' '}
                              × {line.quantity}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                (up to {remaining} left to allocate)
                              </Typography>
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              label="This box"
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, max: remaining }}
                              value={defaultBoxQtyForLine(
                                data.shipping?.fulfillment,
                                line,
                                newBoxQty,
                                key
                              )}
                              onChange={(e) =>
                                setNewBoxQty((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                            />
                          </Stack>
                        );
                      })}
                    </Stack>
                    <Button
                      variant="outlined"
                      disabled={shipBusy}
                      onClick={() => void handleCreateShipment()}
                    >
                      Create draft box
                    </Button>
                  </>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Items
            </Typography>
            {customTextEditError && customTextEditLineId && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {customTextEditError}
              </Alert>
            )}
            <Stack spacing={1}>
              {data.lines.map((line, idx) => {
                const lineKey = line.order_line_item_id ?? '';
                const statusAllowsEdit = orderStatusAllowsCustomTextEdit(
                  data.order_status_name,
                  data.order_status_id
                );
                const showCustomTextEdit =
                  canApprove &&
                  statusAllowsEdit &&
                  lineKey.length > 0 &&
                  lineAllowsCustomTextEditor(line);
                const isEditing = customTextEditLineId === lineKey;
                const maxLines = line.max_text_lines ?? 10;
                const maxLen = line.max_text_length ?? 500;
                return (
                  <Box
                    key={
                      line.order_line_item_id ?? `${line.online_item_id}-${idx}`
                    }
                    sx={{ mb: 1 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Typography variant="body2">
                        {line.item_display_name?.trim() ||
                          line.item_name?.trim() ||
                          'Item'}{' '}
                        × {line.quantity}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">
                          {formatPrice(line.subtotal)}
                        </Typography>
                        {showCustomTextEdit && !isEditing && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setCustomTextEditLineId(lineKey);
                              setCustomTextDraft(
                                (line.custom_text_lines ?? []).join('\n')
                              );
                              setCustomTextEditError(null);
                            }}
                          >
                            Edit text
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    {isEditing && (
                      <Box sx={{ mt: 1, pl: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Custom text lines"
                          helperText={`One line per row (max ${maxLines} lines, ${maxLen} characters each). Empty lines are ignored.`}
                          value={customTextDraft}
                          onChange={(e) => setCustomTextDraft(e.target.value)}
                          disabled={customTextEditBusy}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={customTextEditBusy}
                            onClick={() => void saveCustomTextLines(lineKey)}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            disabled={customTextEditBusy}
                            onClick={() => {
                              setCustomTextEditLineId(null);
                              setCustomTextDraft('');
                              setCustomTextEditError(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    )}
                    {!isEditing &&
                      line.custom_text_lines &&
                      line.custom_text_lines.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                          sx={{ mt: 0.5, pl: 1 }}
                        >
                          {line.custom_text_lines.map((t, i) => (
                            <span key={i}>
                              {i > 0 ? ' · ' : ''}
                              {t}
                            </span>
                          ))}
                        </Typography>
                      )}
                    {line.personalization_display_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.5, pl: 1 }}
                      >
                        Member: {line.personalization_display_name}
                        {line.personalization_first_name
                          ? ` — Name on item: ${line.personalization_first_name}`
                          : ''}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>

            {refundSuccess && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                onClose={() => setRefundSuccess(null)}
              >
                {refundSuccess}
              </Alert>
            )}

            {freightEditError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                onClose={() => setFreightEditError(null)}
              >
                {freightEditError}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              spacing={2}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Subtotal</Typography>
                  <Typography>{formatPrice(data.subtotal)}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography>Freight</Typography>
                  {freightEditing ? (
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                        value={freightDraft}
                        onChange={(e) => {
                          setFreightDraft(e.target.value);
                          setFreightNoCharge(false);
                          setFreightEditError(null);
                        }}
                        sx={{ width: 120 }}
                        disabled={freightEditBusy || freightNoCharge}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={freightNoCharge}
                            disabled={freightEditBusy}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (checked) {
                                if (!freightNoCharge && freightDraft !== '0') {
                                  setFreightAmountBeforeNoCharge(freightDraft);
                                }
                                setFreightNoCharge(true);
                                setFreightDraft('0');
                              } else {
                                setFreightNoCharge(false);
                                setFreightDraft(
                                  freightAmountBeforeNoCharge || '0'
                                );
                              }
                              setFreightEditError(null);
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            No freight charge
                          </Typography>
                        }
                        sx={{ mr: 0 }}
                      />
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>
                        {formatPrice(data.shipping_amount)}
                        {data.shipping_amount === 0 ? ' (No charge)' : ''}
                      </Typography>
                      {showFreightEdit && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={startFreightEdit}
                        >
                          Edit freight
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
                {freightEditing && (
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ mt: 1 }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      disabled={freightEditBusy}
                      onClick={() => void handleFreightSave()}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      disabled={freightEditBusy}
                      onClick={resetFreightEdit}
                    >
                      Cancel
                    </Button>
                  </Stack>
                )}
                {freightEditing && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    New total: {formatPrice(freightPreviewTotal)}. Payment memos
                    or refunds may be needed if recorded payment does not match.
                  </Typography>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Tax</Typography>
                  <Typography>{formatPrice(data.tax)}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatPrice(
                      freightEditing ? freightPreviewTotal : data.total
                    )}
                  </Typography>
                </Stack>
                {showRefundButton && (data.stripe_amount_refunded ?? 0) > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Refunded to date:{' '}
                    {formatPrice(data.stripe_amount_refunded ?? 0)}
                  </Typography>
                )}
              </Box>
              {showRefundButton && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={openRefundDialog}
                  sx={{ alignSelf: { sm: 'flex-end' }, flexShrink: 0 }}
                >
                  Refund
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={refundDialogOpen}
        onClose={() => !refundBusy && setRefundDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Issue Stripe refund</DialogTitle>
        <DialogContent>
          {data && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Captured: {formatPrice(data.stripe_amount_captured ?? 0)}
                <br />
                Already refunded:{' '}
                {formatPrice(data.stripe_amount_refunded ?? 0)}
                <br />
                Refundable: {formatPrice(data.stripe_refundable_amount ?? 0)}
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={refundMode}
                  onChange={(e) =>
                    setRefundMode(
                      e.target.value === 'partial' ? 'partial' : 'full'
                    )
                  }
                >
                  <FormControlLabel
                    value="full"
                    control={<Radio />}
                    label={`Full refund (${formatPrice(data.stripe_refundable_amount ?? 0)})`}
                  />
                  <FormControlLabel
                    value="partial"
                    control={<Radio />}
                    label="Partial refund"
                  />
                </RadioGroup>
              </FormControl>
              {refundMode === 'partial' && (
                <TextField
                  label="Refund amount"
                  size="small"
                  fullWidth
                  value={partialRefundAmount}
                  onChange={(e) => setPartialRefundAmount(e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  helperText={`Maximum ${formatPrice(data.stripe_refundable_amount ?? 0)}`}
                />
              )}
              {refundError && <Alert severity="error">{refundError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRefundDialogOpen(false)}
            disabled={refundBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void submitRefund()}
            disabled={refundBusy}
          >
            {refundBusy ? 'Processing…' : 'Confirm refund'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={memoResolveOpen}
        onClose={() => !memoResolveBusy && setMemoResolveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Resolve payment memo</DialogTitle>
        <DialogContent>
          {data?.payment_memo && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Balance remaining:{' '}
                {formatPrice(data.payment_memo.balance_remaining)}
                <br />
                {data.payment_memo.memo_type === 'debit'
                  ? 'Records payment received from member.'
                  : 'Records credit issued to member.'}
              </Typography>
              <TextField
                label="Amount applied"
                value={memoResolveAmount}
                onChange={(e) => setMemoResolveAmount(e.target.value)}
                size="small"
                fullWidth
                inputMode="decimal"
              />
              {data.payment_memo.memo_type === 'debit' && (
                <TextField
                  label="Check number"
                  value={memoResolveCheckNumber}
                  onChange={(e) => setMemoResolveCheckNumber(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  inputProps={{ maxLength: 32 }}
                  helperText="Creates a check payment row for finance (amount applied on this check)."
                />
              )}
              <TextField
                label="Notes (optional)"
                value={memoResolveNotes}
                onChange={(e) => setMemoResolveNotes(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
              {memoResolveError && (
                <Alert severity="error">{memoResolveError}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMemoResolveOpen(false)}
            disabled={memoResolveBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitMemoResolve()}
            disabled={memoResolveBusy}
          >
            {memoResolveBusy ? 'Saving…' : 'Apply resolution'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={freightConfirmOpen}
        onClose={() => !freightEditBusy && setFreightConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm freight change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 1 }}>
            Order total will change from {formatPrice(data?.total ?? 0)} to{' '}
            {formatPrice(freightPendingTotal ?? 0)}. Recorded payment may not
            match. You may need a payment memo or refund separately. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFreightConfirmOpen(false);
              setFreightPendingTotal(null);
            }}
            disabled={freightEditBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const freight = freightNoCharge ? 0 : parseFloat(freightDraft);
              if (
                Number.isNaN(freight) ||
                freight < 0 ||
                freightPendingTotal == null
              ) {
                return;
              }
              void persistFreight(freight, freightPendingTotal);
            }}
            disabled={freightEditBusy}
          >
            {freightEditBusy ? 'Saving…' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
